/**
 * AuditSection (P10 §4.8)
 * Historial paginado de accesos al expediente del cliente.
 * Usa tokens semánticos para dark-mode.
 */
import { Eye, Download, FileText, Search, Clock } from 'lucide-react';
import type { ReactNode } from 'react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { useExpedienteAuditLog } from '../../hooks';

interface AuditSectionProps {
  directoryId: string;
  enabled: boolean;
}

const ACTION_LABELS: Record<string, { label: string; icon: ReactNode; tone: 'info' | 'success' | 'warning' }> = {
  VIEW_SEARCH_RESULT:    { label: 'Resultado de búsqueda', icon: <Search size={12} />, tone: 'info' },
  VIEW_RESUMEN:          { label: 'Pantalla principal',    icon: <Eye size={12} />,    tone: 'info' },
  VIEW_IDENTITY:         { label: 'Identidad',              icon: <Eye size={12} />,    tone: 'info' },
  VIEW_CONTRACTS:        { label: 'Contratos',              icon: <FileText size={12} />, tone: 'info' },
  VIEW_CONTRACT_DETAIL:  { label: 'Detalle de contrato',    icon: <FileText size={12} />, tone: 'info' },
  VIEW_DOCUMENT:         { label: 'Documento',              icon: <Download size={12} />, tone: 'warning' },
  VIEW_PROFILE:          { label: 'Perfil financiero',      icon: <Eye size={12} />,    tone: 'info' },
  VIEW_OFFERS:           { label: 'Ofertas',                icon: <Eye size={12} />,    tone: 'info' },
  VIEW_APPLICATIONS:     { label: 'Solicitudes',            icon: <Eye size={12} />,    tone: 'info' },
  VIEW_PAYMENTS:         { label: 'Pagos',                  icon: <Eye size={12} />,    tone: 'info' },
  VIEW_AUDIT_LOG:        { label: 'Auditoría',              icon: <Clock size={12} />,  tone: 'info' },
};

export function AuditSection({ directoryId, enabled }: AuditSectionProps) {
  const { items, pagination, isLoading, isError, page, setPage } = useExpedienteAuditLog(directoryId, enabled);

  if (!enabled) return null;
  if (isLoading) return <div className="p-4 text-sm text-muted-foreground">Cargando auditoría…</div>;
  if (isError) return <div className="p-4 text-sm text-red-600 dark:text-red-400">Error al cargar la auditoría.</div>;
  if (items.length === 0) {
    return <div className="p-4 text-sm text-muted-foreground">Sin accesos registrados.</div>;
  }

  const totalPages = pagination?.total_pages ?? 1;
  const totalItems = pagination?.total_items ?? 0;

  return (
    <div className="p-4 space-y-3">
      <p className="text-xs text-muted-foreground">
        {totalItems} {totalItems === 1 ? 'acceso registrado' : 'accesos registrados'} · página {page} de {totalPages}
      </p>

      <div className="overflow-hidden rounded-md border border-border">
        <table className="w-full text-sm">
          <thead className="bg-muted text-xs uppercase text-muted-foreground">
            <tr>
              <th className="text-left px-3 py-2">Fecha</th>
              <th className="text-left px-3 py-2">Usuario</th>
              <th className="text-left px-3 py-2">Rol</th>
              <th className="text-left px-3 py-2">Acción</th>
              <th className="text-left px-3 py-2">IP</th>
            </tr>
          </thead>
          <tbody>
            {items.map((entry, idx) => {
              const meta = ACTION_LABELS[entry.action] ?? { label: entry.action, icon: null, tone: 'info' as const };
              return (
                <tr key={`${entry.timestamp}-${entry.user_id}-${idx}`} className="border-t border-border transition hover:bg-muted">
                  <td className="px-3 py-2 whitespace-nowrap text-foreground">{formatTimestamp(entry.timestamp)}</td>
                  <td className="px-3 py-2 truncate max-w-[200px] text-foreground" title={entry.user_email}>{entry.user_email || entry.user_id}</td>
                  <td className="px-3 py-2 text-muted-foreground">{entry.user_role}</td>
                  <td className="px-3 py-2">
                    <Badge variant={meta.tone}>
                      {meta.icon && <span className="inline-block mr-1 align-middle">{meta.icon}</span>}
                      {meta.label}
                    </Badge>
                  </td>
                  <td className="px-3 py-2 text-muted-foreground font-mono text-xs">{entry.ip_address}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>
            Anterior
          </Button>
          <span className="text-xs text-muted-foreground">página {page} de {totalPages}</span>
          <Button variant="ghost" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>
            Siguiente
          </Button>
        </div>
      )}
    </div>
  );
}

function formatTimestamp(iso: string): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'medium' });
  } catch {
    return iso;
  }
}
